import PropTypes from 'prop-types';
import { useState, useCallback, useEffect, useMemo } from 'react';
import { useI18n } from '../../../../context/I18nContext';
import { useRoomCreate } from '../../../../hooks/useRoomCreate';
import { useAvatar } from '../../../../context/AvatarContext';
import { useMatrixClient } from '../../../../context/MatrixContext';
import logger from '../../../../utils/logger';
import TopBar from './TopBar';
import ContactsSelector from './ContactsSelector';
import Messages from './Messages/Messages';
import './NewChatView.css';

const NewChatView = ({
  onSave = () => {},
  onCancel = () => {},
  isChannel = false,
}) => {
  const { t } = useI18n();
  const client = useMatrixClient();
  const { getFallbackAvatarsFromMembers } = useAvatar();

  // Hook to manage contact selection state and avatar (for new room creation)
  const {
    selectedContacts,
    getDefaultTitle,
    toggleContact,
    removeContact,
    maxSelections,
    selectedAvatarOption,
    selectedAvatarFile,
    selectDefaultAvatar,
    selectPresetAvatar,
    selectCustomAvatar,
    currentUser,
    canCreate,
    topic,
    externalUsers,
    addExternalUser,
  } = useRoomCreate({
    maxSelections: 1,
    minParticipantsSelected: 0,
    allowEmptyRoom: isChannel,
  });

  const [title, setTitle] = useState('');
  const [initialMessageText, setInitialMessageText] = useState('');
  const [avatarUrls, setAvatarUrls] = useState([]);
  const [defaultAvatarUrls, setDefaultAvatarUrls] = useState([]);
  const [isPrivateRoom, setIsPrivateRoom] = useState(true);
  const [isCreatingRoom, setIsCreatingRoom] = useState(false);

  const defaultTitle = getDefaultTitle();
  const myUserId = client?.getUserId();

  // Compute member avatars (including current user) for display
  useEffect(() => {
    const computeAvatars = async () => {
      if (!client || !myUserId || !currentUser) return;

      // Build members array - ALWAYS include current user
      const members = [];
      
      // Add current user first
      members.push({
        userId: myUserId,
        name: currentUser.displayName || myUserId,
        rawDisplayName: currentUser.rawDisplayName || currentUser.displayName || myUserId,
      });
      
      // Add selected contacts
      selectedContacts.forEach(contact => {
        members.push({
          userId: contact.id,
          name: contact.name,
          rawDisplayName: contact.name,
        });
      });

      if (members.length > 0) {
        const fallbacks = await getFallbackAvatarsFromMembers(members, 96);
        const urls = fallbacks.map(desc => desc.url).filter(Boolean);
        setAvatarUrls(urls);
        setDefaultAvatarUrls(urls);
      } else {
        setAvatarUrls([]);
        setDefaultAvatarUrls([]);
      }
    };

    computeAvatars();
  }, [client, myUserId, currentUser, selectedContacts, getFallbackAvatarsFromMembers]);

  const canSendMessage = useMemo(() => {
    if (isChannel) {
      // Channels: can send if there's a message (creates self-group)
      return initialMessageText.trim().length > 0;
    } else {
      // DMs: require at least 1 participant AND optional message
      return selectedContacts.length > 0;
    }
  }, [isChannel, initialMessageText, selectedContacts.length]);

  const handleCancel = useCallback(() => {
    // Don't reset state here - let the navigation guard handle it
    // The parent component will navigate away, and state cleanup
    // should only happen if the user confirms in the discard modal
    onCancel();
  }, [onCancel]);

  const createRoomWithData = useCallback(() => {
    // Prevent duplicate room creation if already in progress
    if (isCreatingRoom) {
      logger.debug('[NewChatView] Room creation already in progress, ignoring duplicate request');
      return;
    }

    logger.debug('[NewChatView] Creating room with current data');
    setIsCreatingRoom(true);

    const finalTitle = (title.trim() !== '') ? title : undefined;
    const conversationData = {
      title: finalTitle,
      topic: topic.trim(),
      selectedContacts,
      initialMessageText: initialMessageText.trim(),
      selectedAvatarOption,
      selectedAvatarFile,
      isPublicRoom: isChannel ? !isPrivateRoom : undefined, // Only include for channels
    };
    logger.debug('[NewChatView] Conversation data:', {
      title: conversationData.title,
      topic: conversationData.topic,
      selectedContactsCount: conversationData.selectedContacts.length,
      selectedContactIds: conversationData.selectedContacts.map(c => c.id),
      hasInitialMessage: !!conversationData.initialMessageText,
      avatarOption: conversationData.selectedAvatarOption,
      isPublicRoom: conversationData.isPublicRoom,
    });
    onSave(conversationData, () => setIsCreatingRoom(false));
  }, [isCreatingRoom, title, topic, selectedContacts, initialMessageText, selectedAvatarOption, selectedAvatarFile, isChannel, isPrivateRoom, onSave]);

  const handleSendMessage = useCallback(() => {
    logger.debug('[NewChatView] handleSendMessage - Creating room via send message');
    createRoomWithData();
  }, [createRoomWithData]);

  const onSaveClick = useCallback(() => {
    logger.debug('[NewChatView] onSaveClick - User clicked save button');
    createRoomWithData();
  }, [createRoomWithData]);

  return (
    <div className="new-chat-view">
      <div className="new-chat-top-bar-container">
        <TopBar
          title={title}
          topic={topic}
          isEditMode={true}
          setTitle={setTitle}
          defaultTitle={defaultTitle}
          defaultTopic='Set a Topic'
          onSaveClick={onSaveClick}
          onCancelClick={handleCancel}
          isCreating={true}
          canSave={canCreate}
          selectedAvatarOption={selectedAvatarOption}
          selectedAvatarFile={selectedAvatarFile}
          avatarUrls={avatarUrls}
          defaultAvatarUrls={defaultAvatarUrls}
          selectDefaultAvatar={selectDefaultAvatar}
          selectPresetAvatar={selectPresetAvatar}
          selectCustomAvatar={selectCustomAvatar}
        />
      </div>
      <div className="new-chat-content">
        <div className="new-chat-messages-container">
          <Messages
            roomId={null}
            messages={[]}
            placeholder={t('ChatViewFooter.sendMessagePlaceholder')}
            disabled={false}
            disableSend={!canSendMessage}
            allowEmptySend={isChannel}
            value={initialMessageText}
            onSetMessage={setInitialMessageText}
            onSendMessage={handleSendMessage}
          />
        </div>
        <div className="new-chat-contacts-container">
          <ContactsSelector
            selectedContacts={selectedContacts}
            onRemoveContact={removeContact}
            onToggleContact={toggleContact}
            onAddExternalUser={addExternalUser}
            maxSelections={maxSelections}
            showRoomTypeToggle={isChannel}
            isPrivateRoom={isPrivateRoom}
            onRoomTypeChange={setIsPrivateRoom}
            additionalUsers={externalUsers}
          />
        </div>
      </div>
    </div>
  );
};

NewChatView.propTypes = {
  onSave: PropTypes.func,
  onCancel: PropTypes.func,
  isChannel: PropTypes.bool,
};

export default NewChatView;
